import { type FC, useEffect } from 'react';
import useMeasure from 'react-use-measure';
import cn from 'classnames';
import {
  animate,
  type AnimationPlaybackControls,
  motion,
  type MotionValue,
  useMotionValue,
} from 'framer-motion';

import css from './styles.module.scss';

import cloudPng from '/img/cloud.png';
import cloudWebp from '/img/cloud.webp';

interface CloudsAnimationProps {
  className?: string;
}

const CloudsAnimation: FC<CloudsAnimationProps> = ({ className }) => {
  const [firstWrapperRef, { width: firstWrapperWidth }] = useMeasure();
  const [secondWrapperRef, { width: secondWrapperWidth }] = useMeasure();
  const xTranslation1 = useMotionValue(0);
  const xTranslation2 = useMotionValue(0);

  const animationFunc = (
    width: number,
    xTranslation: MotionValue<number>,
    duration: number,
  ): AnimationPlaybackControls => {
    const finalPosition = -width / 2;
    const controls = animate(xTranslation, [0, finalPosition], {
      ease: 'linear',
      duration,
      repeat: Number.POSITIVE_INFINITY,
      repeatType: 'loop',
      repeatDelay: 0,
    });

    return controls;
  };

  useEffect(() => {
    const controls = animationFunc(firstWrapperWidth, xTranslation1, 15);

    return controls.stop;
  }, [xTranslation1, firstWrapperWidth]);

  useEffect(() => {
    const controls = animationFunc(secondWrapperWidth, xTranslation2, 30);

    return controls.stop;
  }, [xTranslation2, secondWrapperWidth]);

  return (
    <div className={cn(css.clouds, className)}>
      <motion.div
        style={{ x: xTranslation1 }}
        ref={firstWrapperRef}
        className="absolute left-0 -top-[3.57%] flex h-[68.93%] w-[200%]"
      >
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={`cloud-${index}-first`} className="w-full h-full">
            <picture
              style={{
                transform: 'translateX(89.23%)',
              }}
              key={index}
              className="absolute h-full aspect-297/193"
            >
              <source srcSet={cloudWebp} type="image/webp" />
              <img className="w-full h-full" src={cloudPng} alt="cloud" />
            </picture>
          </div>
        ))}
      </motion.div>
      <motion.div
        style={{ x: xTranslation2 }}
        ref={secondWrapperRef}
        className="absolute left-0 flex top-[17.14%] h-[43.93%]  w-[200%]"
      >
        {Array.from({ length: 2 }).map((_, index) => {
          return (
            <div key={`cloud-${index}-second`} className="h-full w-full">
              <picture
                style={{
                  transform: 'translateX(71.43%)',
                }}
                key={index}
                className="absolute h-full aspect-189/123"
              >
                <source srcSet={cloudWebp} type="image/webp" />
                <img className="w-full h-full" src={cloudPng} alt="cloud" />
              </picture>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

export { CloudsAnimation };
